import React, { useCallback, useMemo, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { DynamicTableV2 } from '@packages/table-builder';
import type { DynamicTableV2Handle } from '@packages/table-builder';
import type { InternalActionDef } from '@packages/table-builder/core/schema/types';
import type { ActionContext } from '@packages/table-builder/hooks/useActionExecutor';
import Heading from '../../../components/typography';
import AlertPopup from '../../../components/alert-popup';
import { PageHeader } from '../company-profile/profile.styled';
import { usePostPptAccessListImport, useWealthlaneCustomerApi } from '../../../hooks';
import { axiosInstance } from '../../../services/apiInterceptor';
import useBlobDownload from '../../../hooks/custom-hooks/useBlobDownload';
import usePermissionCheck from '../../../hooks/custom-hooks/usePermissionCheck';
import { selectAuthRoutesList } from '../../configuration/routeSlice';
import {
  type Wealthlane_CustomerService_Dtos_AdvisorApi_GetPptAccessListInput,
  Wealthlane_AdvisorApi_PptAccessListStatus,
} from '@wealthlane/api-services/wealthlane-customer-service';
import { pptAccessListTableSchema } from './schema';

const EXPORT_URL = '/api/customer/api/app/ppt-access-list-issuer/export';
const IMPORT_INPUT_ID = 'ppt-access-list-import-input';

const PPT_ACCESS_LIST_PERMISSION = 'CustomerService.Customer.ManagePptAccessList';
const IMPORT_JOB_SUBMITTED_MESSAGE = 'Participant Access Import job submitted';

type ExportBodyOptions = {
  ids?: string[];
  deselectedIds?: string[];
};

const buildExportBody = (
  filters: Record<string, unknown> = {},
  options: ExportBodyOptions = {},
): Wealthlane_CustomerService_Dtos_AdvisorApi_GetPptAccessListInput => {
  const body: Wealthlane_CustomerService_Dtos_AdvisorApi_GetPptAccessListInput = {};

  const advisorCompanyIds = filters.advisorCompanyIds;
  if (Array.isArray(advisorCompanyIds)) {
    const ids = advisorCompanyIds.map(String).filter(Boolean);
    if (ids.length > 0) body.advisorCompanyIds = ids;
  }

  if (typeof filters.participantName === 'string' && filters.participantName.trim()) {
    body.participantName = filters.participantName.trim();
  }

  if (typeof filters.employeeId === 'string' && filters.employeeId.trim()) {
    body.employeeId = filters.employeeId.trim();
  }

  if (filters.status != null && filters.status !== '') {
    body.status = Number(filters.status) as Wealthlane_AdvisorApi_PptAccessListStatus;
  }

  if (options.ids?.length) {
    body.ids = options.ids;
  }

  if (options.deselectedIds?.length) {
    body.deselectedIds = options.deselectedIds;
  }

  return body;
};

const PptAccessListPage = (): JSX.Element => {
  const tableRef = useRef<DynamicTableV2Handle>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const navigate = useNavigate();
  const { handleRequest } = useWealthlaneCustomerApi();
  const { downloadBlob, isLoading: isExportLoading } = useBlobDownload();
  const { mutate: importFile, isLoading: isImportLoading } = usePostPptAccessListImport(handleRequest);
  const userAuthList = useSelector(selectAuthRoutesList);
  const grantedPolicies = userAuthList?.auth?.grantedPolicies ?? {};
  const [isImportSubmitModalOpen, setIsImportSubmitModalOpen] = useState(false);
  const [importTaskReferenceNo, setImportTaskReferenceNo] = useState<string>();
  const [importSubmitMessage, setImportSubmitMessage] = useState(IMPORT_JOB_SUBMITTED_MESSAGE);

  const hasPolicy = useCallback(
    (suffix?: string) => Boolean(grantedPolicies[`${PPT_ACCESS_LIST_PERMISSION}${suffix ?? ''}`]),
    [grantedPolicies],
  );

  const canView = hasPolicy();
  const canTrackJob = usePermissionCheck({
    type: 'View',
    feature: 'Customer',
    permission: 'TaskManagerConsole',
  });
  const canImport =
    hasPolicy('.Create') || hasPolicy('.Edit') || hasPolicy('.Delete');

  const tableSchema = useMemo(() => {
    const toolbarActions = [];
    const bulkActions = [];

    if (canImport) {
      toolbarActions.push({ id: 'IMPORT_PPT_ACCESS_LIST', label: 'Import', icon: 'upload' });
    }

    if (canView) {
      toolbarActions.push({
        label: 'Downloads',
        icon: 'download',
        type: 'download',
        children: [
          { id: 'EXPORT_PPT_ACCESS_LIST', label: 'Export List' },
          { id: 'DOWNLOAD_PPT_ACCESS_TEMPLATE', label: 'Download Template' },
        ],
      });

      bulkActions.push({
        id: 'EXPORT_SELECTED_PPT_ACCESS_LIST',
        label: 'Export Selected',
        icon: 'download',
      });
    }

    return {
      ...pptAccessListTableSchema,
      actions: {
        ...pptAccessListTableSchema.actions,
        bulk: bulkActions,
        toolbar: {
          ...pptAccessListTableSchema.actions?.toolbar,
          secondary: toolbarActions,
        },
      },
    };
  }, [canImport, canView]);

  const handleExport = useCallback(
    async (
      filters: Record<string, unknown> = {},
      options: ExportBodyOptions = {},
    ) => {
      await downloadBlob({
        url: EXPORT_URL,
        method: 'POST',
        data: buildExportBody(filters, options),
        defaultFileName: 'ppt-access-list.xlsx',
        successMessage: 'PPT access list exported successfully.',
      });
    },
    [downloadBlob],
  );

  const onActionIntercept = useCallback(
    async (action: InternalActionDef, context: ActionContext): Promise<boolean> => {
      if (action.id === 'IMPORT_PPT_ACCESS_LIST') {
        if (!isImportLoading) fileInputRef.current?.click();
        return false;
      }

      const activeFilters = (context.tableQuery ?? {}) as Record<string, unknown>;

      if (action.id === 'EXPORT_PPT_ACCESS_LIST') {
        if (!isExportLoading) await handleExport(activeFilters);
        return false;
      }

      if (action.id === 'DOWNLOAD_PPT_ACCESS_TEMPLATE') {
        if (!isExportLoading) await handleExport({});
        return false;
      }

      if (action.id === 'EXPORT_SELECTED_PPT_ACCESS_LIST') {
        if (isExportLoading) return false;

        if (context.selectAllActive) {
          await handleExport(activeFilters, {
            deselectedIds: context.deselectedRowIds,
          });
        } else {
          const selectedIds = context.selectedKeys ?? [];
          if (selectedIds.length === 0) return false;
          await handleExport({}, { ids: selectedIds });
        }

        return false;
      }

      return true;
    },
    [handleExport, isExportLoading, isImportLoading],
  );

  const closeImportSubmitModal = (): void => {
    setIsImportSubmitModalOpen(false);
    setImportTaskReferenceNo(undefined);
  };

  const handleTrackImportJob = (): void => {
    if (!importTaskReferenceNo) {
      closeImportSubmitModal();
      return;
    }

    navigate(
      `/task-management-console?filter=${encodeURIComponent(importTaskReferenceNo)}&view_details=true`,
    );
    closeImportSubmitModal();
  };

  const handleImportChange = (event: React.ChangeEvent<HTMLInputElement>): void => {
    const file = event.target.files?.[0];
    if (file == null) return;

    importFile(file, {
      onSuccess: (response) => {
        if (response?.success === false) return;

        const data = response?.data;

        setImportSubmitMessage(response?.message ?? IMPORT_JOB_SUBMITTED_MESSAGE);
        setImportTaskReferenceNo(data?.taskReferenceNo ?? undefined);
        setIsImportSubmitModalOpen(true);
        tableRef.current?.refreshData();
      },
      onSettled: () => {
        event.target.value = '';
      },
    });
  };

  return (
    <>
      <PageHeader data-page='pptAccessList' id='pageContainer'>
        <Heading variant='h3' title='Advisor PPT Access' id='pageTitle' />
        <Heading
          variant='body-s'
          title='Manage which participants advisors can access. Import and export access lists in bulk.'
          id='pageDescription'
        />
      </PageHeader>

      <input
        ref={fileInputRef}
        id={IMPORT_INPUT_ID}
        className='hidden'
        type='file'
        accept='.xlsx,.xls'
        onChange={handleImportChange}
      />

      <DynamicTableV2
        ref={tableRef}
        schema={tableSchema}
        axiosInstance={axiosInstance}
        onActionIntercept={onActionIntercept}
      />

      {isImportSubmitModalOpen && (
        <AlertPopup
          id='pptAccessListImportSubmitModal'
          isShow={isImportSubmitModalOpen}
          closeModal={closeImportSubmitModal}
          cancelLabel='Okay'
          customTitle={importSubmitMessage}
          showTaskReferenceNo={Boolean(importTaskReferenceNo)}
          taskRefrenceNo={importTaskReferenceNo}
          display={canTrackJob && Boolean(importTaskReferenceNo)}
          ConfirmTitle='Track Job'
          handleSubmit={handleTrackImportJob}
          icon={false}
        />
      )}
    </>
  );
};

export default PptAccessListPage;
